import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    ColorSwatchPicker,
    NCoreUIKitTheme,
    RadioButton,
    InputGroup,
    RadioGroup,
    TextInput,
    Stepper,
    Button,
    Field,
    Icon,
    Chip,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    CircleCheck,
    ArrowRight,
    CircleHelp,
    ArrowLeft,
    Info
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const ArrowRightIcon = createKitIcon(ArrowRight);

const CircleCheckIcon = createKitIcon(CircleCheck);

const CircleHelpIcon = createKitIcon(CircleHelp);

const ArrowLeftIcon = createKitIcon(ArrowLeft);

const STEPS = [
    "profile",
    "workspace",
    "team",
    "plan"
];

const SIZES = [
    "solo",
    "small",
    "growing",
    "large"
];

const USES = [
    "projects",
    "support",
    "sales",
    "marketing",
    "engineering"
];

const COLORS = [
    {
        key: "teal",
        index: 1
    },
    {
        key: "brick",
        index: 2
    },
    {
        key: "blue",
        index: 3
    },
    {
        key: "mustard",
        index: 4
    },
    {
        key: "purple",
        index: 5
    },
    {
        key: "green",
        index: 6
    },
    {
        key: "cyan",
        index: 7
    },
    {
        key: "rose",
        index: 8
    }
] as const;

const MIN_ADDRESS_LENGTH = 3;

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
        width: "100%"
    },
    note: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row"
    },
    page: {
        alignItems: "center",
        flexGrow: 1
    },
    sizes: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    uses: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    compactStepper: {
        width: "100%"
    },
    size: {
        flexBasis: 0,
        flexGrow: 1
    },
    card: {
        width: "100%"
    },
    stepper: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const Onboarding01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        name,
        setName
    ] = useState(t("blocks.onboarding01.nameValue"));

    const [
        address,
        setAddress
    ] = useState(t("blocks.onboarding01.addressValue"));

    const [
        size,
        setSize
    ] = useState("small");

    const [
        uses,
        setUses
    ] = useState<Array<string>>([
        "projects",
        "marketing"
    ]);

    const [
        color,
        setColor
    ] = useState(colors.chart.categorical[1]);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isAddressValid = address.length >= MIN_ADDRESS_LENGTH;

    const handleUseToggle = (use: string) => () => setUses(
        uses.includes(use) ? uses.filter((key) => key !== use) : [
            ...uses,
            use
        ]
    );

    const handleSizeChange = (value: string) => setSize(value);

    const handleColorChange = (value: string) => setColor(value);

    const renderStepper = () => <View
        style={[
            stylesheet.stepper,
            {
                maxWidth: shapeConfigs.contentMaxWidth * 0.75
            }
        ]}
    >
        <Stepper
            accessibilityLabel={t("blocks.onboarding01.stepsTitle")}
            steps={STEPS.map((key) => ({
                title: t(`blocks.onboarding01.step_${key}`),
                key
            }))}
            activeIndex={1}
            size="small"
        />
    </View>;

    return <View
        style={[
            stylesheet.page,
            {
                backgroundColor: colors.content.container.mid,
                paddingBottom: spaces.spacingLg,
                gap: spaces.spacingSm
            }
        ]}
    >
        <View
            style={[
                stylesheet.bar,
                {
                    paddingHorizontal: spaces.spacingLg,
                    paddingVertical: spaces.spacingMd,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <BrandMark/>
            {isCompact ? null : renderStepper()}
            <Button
                title={t("blocks.onboarding01.help")}
                onPress={handlePress}
                icon={CircleHelpIcon}
                variant="ghost"
                type="neutral"
            />
        </View>
        {
            isCompact ?
                <View
                    style={[
                        stylesheet.compactStepper,
                        {
                            paddingHorizontal: spaces.spacingMd
                        }
                    ]}
                >
                    {renderStepper()}
                </View>
            :
                null
        }
        <View
            style={[
                stylesheet.page,
                {
                    paddingHorizontal: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Card
                style={[
                    stylesheet.card,
                    {
                        maxWidth: shapeConfigs.contentMaxWidth * 0.75
                    }
                ]}
                variant="elevated"
            >
                <Card.Header>
                    <Card.Title
                        headingLevel={2}
                    >
                        {t("blocks.onboarding01.title")}
                    </Card.Title>
                    <Card.Description>
                        {t("blocks.onboarding01.description")}
                    </Card.Description>
                </Card.Header>
                <Card.Content>
                    <Field.Group>
                        <TextInput
                            title={t("blocks.onboarding01.name")}
                            spreadBehaviour="stretch"
                            onChangeText={setName}
                            value={name}
                        />
                        <InputGroup
                            hintText={t(isAddressValid ? "blocks.onboarding01.addressOk" : "blocks.onboarding01.addressShort")}
                            type={isAddressValid ? "success" : "danger"}
                            title={t("blocks.onboarding01.address")}
                            spreadBehaviour="stretch"
                            isShowHintTextIcon={true}
                        >
                            <InputGroup.Addon
                                variant="attached"
                            >
                                <InputGroup.Text>
                                    {t("blocks.onboarding01.addressPrefix")}
                                </InputGroup.Text>
                            </InputGroup.Addon>
                            <InputGroup.Input
                                onChangeText={setAddress}
                                autoCapitalize="none"
                                value={address}
                            />
                            {
                                isAddressValid ?
                                    <InputGroup.Addon
                                        icon={CircleCheckIcon}
                                        align="end"
                                    />
                                :
                                    null
                            }
                        </InputGroup>
                        <Field.Set>
                            <Field.Legend>
                                {t("blocks.onboarding01.size")}
                            </Field.Legend>
                            <RadioGroup
                                onValueChange={handleSizeChange}
                                style={stylesheet.sizes}
                                orientation="horizontal"
                                value={size}
                            >
                                {
                                    SIZES.map((key) => <RadioButton
                                        subTitle={t(`blocks.onboarding01.size_${key}_sub`)}
                                        style={[
                                            stylesheet.size,
                                            {
                                                minWidth: shapeConfigs.fieldMinWidth * 0.5
                                            }
                                        ]}
                                        title={t(`blocks.onboarding01.size_${key}`)}
                                        spreadBehaviour="stretch"
                                        variant="card"
                                        value={key}
                                        key={key}
                                    />)
                                }
                            </RadioGroup>
                        </Field.Set>
                        <Field.Set>
                            <Field.Legend>
                                {t("blocks.onboarding01.uses")}
                            </Field.Legend>
                            <View
                                style={[
                                    stylesheet.uses,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                {
                                    USES.map((key) => <Chip
                                        title={t(`blocks.onboarding01.use_${key}`)}
                                        isSelected={uses.includes(key)}
                                        onPress={handleUseToggle(key)}
                                        variant="outline"
                                        type="neutral"
                                        key={key}
                                    />)
                                }
                            </View>
                            <Field.Description>
                                {t("blocks.onboarding01.usesHint")}
                            </Field.Description>
                        </Field.Set>
                        <Field.Set>
                            <Field.Legend>
                                {t("blocks.onboarding01.color")}
                            </Field.Legend>
                            <ColorSwatchPicker
                                items={COLORS.map((item) => ({
                                    title: t(`blocks.onboarding01.color_${item.key}`),
                                    color: colors.chart.categorical[item.index]
                                }))}
                                title={t("blocks.onboarding01.color")}
                                onChange={handleColorChange}
                                onAddPress={handlePress}
                                isAddEnabled={true}
                                value={color}
                            />
                        </Field.Set>
                    </Field.Group>
                </Card.Content>
                <Card.Footer
                    align="between"
                >
                    <Button
                        title={t("blocks.onboarding01.back")}
                        onPress={handlePress}
                        icon={ArrowLeftIcon}
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        title={t("blocks.onboarding01.next")}
                        icon={ArrowRightIcon}
                        onPress={handlePress}
                        iconDirection="end"
                        variant="filled"
                        type="primary"
                    />
                </Card.Footer>
            </Card>
            <View
                style={[
                    stylesheet.note,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Icon
                    color="mid"
                    icon={Info}
                />
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.onboarding01.note")}
                </Text>
            </View>
        </View>
    </View>;
};
export default Onboarding01;
